import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import axios from 'axios';

const CODE_EXPIRY_SECONDS = 15 * 60;

function OrgOnboard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { slug: urlSlug } = useParams();
  const [step, setStep] = useState(1); // 1: info, 2: code, 3: success
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [privacyAck, setPrivacyAck] = useState(false);
  const [error, setError] = useState('');
  const [orgSlug, setOrgSlug] = useState('');
  const [loading, setLoading] = useState(false);
  const [orgName, setOrgName] = useState('');
  const [secondsLeft, setSecondsLeft] = useState(CODE_EXPIRY_SECONDS);

  useEffect(() => {
    if (step !== 2) return;
    if (secondsLeft <= 0) return;
    const timer = setInterval(() => {
      setSecondsLeft(prev => prev <= 1 ? (clearInterval(timer), 0) : prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [step, secondsLeft]);

  const formatTime = useCallback((s) => `${Math.floor(s/60)}:${(s%60).toString().padStart(2,'0')}`, []);

  const handleSendCode = async (e) => {
    e.preventDefault();
    setError('');
    if (!firstName.trim()) { setError('Your name is required.'); return; }
    if (!email.trim()) { setError('Work email is required.'); return; }
    if (!privacyAck) { setError('Please acknowledge the privacy statement.'); return; }

    setLoading(true);
    try {
      // Send verification code (uses existing auth endpoint — no login required)
      await axios.post('/api/auth/send-email-code', { email: email.trim(), isLogin: false });
      setSecondsLeft(CODE_EXPIRY_SECONDS);
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send verification code.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyAndJoin = async (e) => {
    e.preventDefault();
    setError('');
    if (!code.trim()) { setError('Please enter the verification code.'); return; }
    if (secondsLeft <= 0) { setError('Code expired. Please request a new one.'); return; }

    setLoading(true);
    try {
      // Verify the code
      await axios.post('/api/auth/verify-email-code', { email: email.trim(), code: code.trim() });

      // Register as employee and join org
      const res = await axios.post('/api/org/onboard/register-employee', {
        firstName: firstName.trim(),
        email: email.trim(),
        code: code.trim()
      });

      // Store tokens
      localStorage.setItem('accessToken', res.data.accessToken);
      localStorage.setItem('refreshToken', res.data.refreshToken);
      setOrgName(res.data.orgName || '');
      setOrgSlug(res.data.orgSlug || '');
      setStep(3);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to join organization.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', maxWidth: '400px' }}>
        <div className="auth-card">
          <div className="logo">
            <img src="/belong.png" alt="Belong" style={{ height: '64px', marginBottom: '8px' }} />
            <p>Join your organization on Belong</p>
          </div>

          <h2>Employee Registration 🏢</h2>

          {step === 1 && (
            <form onSubmit={handleSendCode}>
              <div style={styles.privacyBox}>
                <label style={styles.checkboxLabel}>
                  <input type="checkbox" checked={privacyAck} onChange={(e) => setPrivacyAck(e.target.checked)} style={{ marginRight: '8px', marginTop: '2px' }} />
                  <span style={{ fontSize: '13px', color: '#374151' }}>
                    I understand my employer cannot see my individual posts or identity
                  </span>
                </label>
              </div>

              <div className="form-group">
                <label>Your First Name *</label>
                <input type="text" placeholder="e.g. Sarah" value={firstName} onChange={(e) => setFirstName(e.target.value)} required />
              </div>

              <div className="form-group">
                <label>Work Email *</label>
                <input type="email" placeholder="you@yourcompany.com" value={email} onChange={(e) => { setEmail(e.target.value); if (error) setError(''); }} required />
                <small style={{ color: '#666', fontSize: '12px', display: 'block', marginTop: '8px' }}>
                  Must be your work email. We'll verify it to connect you with your organization.
                </small>
              </div>

              {error && <div className="error">{error}</div>}

              <button type="submit" className="btn btn-primary" disabled={loading || !email.trim() || !firstName.trim() || !privacyAck}>
                {loading ? 'Sending...' : 'Send Verification Code 📧'}
              </button>
            </form>
          )}

          {step === 2 && (
            <form onSubmit={handleVerifyAndJoin}>
              <p style={{ textAlign: 'center', marginBottom: '16px', color: '#374151', fontSize: '14px' }}>
                We sent a 6-digit code to <strong>{email}</strong>
              </p>

              <div className="form-group">
                <label>Verification Code</label>
                <input type="text" placeholder="123456" value={code} onChange={(e) => { setCode(e.target.value); if (error) setError(''); }} maxLength={6} required />
              </div>

              <div style={{ textAlign: 'center', fontSize: '13px', marginBottom: '16px' }}>
                {secondsLeft > 0 ? (
                  <span style={{ color: secondsLeft < 120 ? '#dc2626' : '#6b7280' }}>⏱ Expires in {formatTime(secondsLeft)}</span>
                ) : (
                  <span style={{ color: '#dc2626' }}>Code expired. <button type="button" onClick={() => { setStep(1); setCode(''); setError(''); }} style={{ background: 'none', border: 'none', color: '#7C3AED', cursor: 'pointer', fontWeight: '600', textDecoration: 'underline' }}>Go back</button></span>
                )}
              </div>

              {error && <div className="error">{error}</div>}

              <button type="submit" className="btn btn-primary" disabled={loading || code.length < 6 || secondsLeft <= 0}>
                {loading ? 'Joining...' : 'Verify & Join Organization 🚀'}
              </button>

              <button type="button" className="btn btn-secondary" onClick={() => { setStep(1); setError(''); setCode(''); }} style={{ marginTop: '10px' }}>
                ← Back
              </button>
            </form>
          )}

          {step === 3 && (
            <div style={{ textAlign: 'center', padding: '16px 0' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>🎉</div>
              <h3 style={{ margin: '0 0 8px', color: '#1f2937' }}>Welcome aboard!</h3>
              <p style={{ color: '#6b7280', marginBottom: '24px' }}>
                You've joined <strong>{orgName}</strong>. You now have access to your organization's anonymous communities.
              </p>
              <div style={styles.trustBadge}>🛡️ Your posts are anonymous and employer-invisible</div>
              <button className="btn btn-primary" onClick={() => { window.location.href = orgSlug ? `/org/${orgSlug}/dashboard` : '/dashboard'; }} style={{ marginTop: '20px' }}>
                Go to Communities →
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {
  privacyBox: { backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '12px', marginBottom: '20px' },
  checkboxLabel: { display: 'flex', alignItems: 'flex-start', cursor: 'pointer' },
  trustBadge: { backgroundColor: '#f0fdf4', color: '#166534', padding: '10px 16px', borderRadius: '8px', fontSize: '13px', fontWeight: '500' },
};

export default OrgOnboard;
